import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ConsentInputFieldData,
  ConsentItemData,
  ConsentItemStateChangeDataEnum,
  ConsentValue,
  CountryData,
  getConsentInputName,
  getValidationSchema,
} from '@ysura/common';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

type ConsentCommunicationCollectionProps = {
  consentItemsCommunicationData: Array<ConsentItemData>;
  consentValues: ConsentValue;
  defaultCountry?: CountryData;
  setConsentValues: (consentValue: ConsentValue) => void;
  setIsSubmitButtonDisabled: (value: boolean) => void;
};
export const ConsentCommunicationCollection = ({
  consentItemsCommunicationData,
  consentValues,
  defaultCountry,
  setConsentValues,
  setIsSubmitButtonDisabled,
}: ConsentCommunicationCollectionProps) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedConsentItems, setExpandedConsentItems] = useState<
    Array<string>
  >([]);
  const [validationSchema, setValidationSchema] = useState<
    Record<string, Yup.AnySchema>
  >(() => {
    return getValidationSchema(
      consentItemsCommunicationData,
      expandedConsentItems,
      defaultCountry
    );
  });

  useEffect(() => {
    const updatedExpandedConsentItems = consentItemsCommunicationData
      .map((consentItem) => {
        if (
          consentItem?.defaultItemState ===
            ConsentItemStateChangeDataEnum.TURN_ON ||
          consentValues?.[consentItem.inputId ?? '']
        ) {
          return consentItem.inputId ?? '';
        }

        return '';
      })
      .filter(Boolean);

    const schema = getValidationSchema(
      consentItemsCommunicationData,
      updatedExpandedConsentItems,
      defaultCountry
    );

    setValidationSchema(schema);

    setExpandedConsentItems([...updatedExpandedConsentItems]);
  }, [consentItemsCommunicationData, consentValues, defaultCountry]);

  const validate = async (field: string, value: string | boolean) => {
    await validationSchema[field]
      ?.validate(value)
      .then(() => {
        delete errors[field];
        setIsSubmitButtonDisabled(!!Object.keys(errors).length);
      })
      .catch((err: Yup.ValidationError) => {
        errors[field] = err.errors[0];
        setErrors(errors);
        setIsSubmitButtonDisabled(!!Object.keys(errors).length);
      });
  };

  const handleConsentItemCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
    consentItem: ConsentItemData
  ) => {
    const keyValue = consentItem?.inputId ?? '';
    const changed: ConsentValue = {};
    changed[keyValue] = event.target.checked;
    if (consentValues && validationSchema) {
      Object.keys(consentValues).forEach((field: string) => {
        validate(field, consentValues?.[field]);
      });
    }
    setConsentValues({ ...consentValues, ...changed });
  };

  const handleConsentInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    consentInput: ConsentInputFieldData
  ) => {
    const keyValue = consentInput?.inputId ?? '';
    const changed: ConsentValue = {};
    changed[keyValue] = event.target.value;
    setConsentValues({ ...consentValues, ...changed });
  };

  const validateConsentInputChange = () => {
    if (consentValues && validationSchema) {
      Object.keys(consentValues).forEach((field: string) => {
        validate(field, consentValues?.[field]);
      });
    }
  };

  return (
    <>
      {consentItemsCommunicationData.map((consentItem) => {
        const consentItemInputId = consentItem?.inputId ?? '';

        return (
          <ConsentItemGrid key={consentItemInputId} size={{ xs: 12 }}>
            <ConsentItemCheckbox
              control={
                <Checkbox
                  id={consentItemInputId}
                  data-testid={`consent-${consentItemInputId}`}
                  checked={!!consentValues?.[consentItemInputId]}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleConsentItemCheckboxChange(event, consentItem);
                  }}
                />
              }
              label={
                <ConsentItemLabel>
                  <Typography variant="caption">
                    {t('components.consentCollection.agreeTo', {
                      consentItem: consentItem?.name,
                    })}
                  </Typography>

                  <ConsentItemDescription variant="caption">
                    {consentItem?.description}
                  </ConsentItemDescription>
                </ConsentItemLabel>
              }
            />

            {consentItemInputId &&
              expandedConsentItems.includes(consentItemInputId) && (
                <Grid container spacing={{ md: 3, xs: 2 }}>
                  {consentItem?.consentInputFields?.map(
                    (consentInput: ConsentInputFieldData) => {
                      const consentInputName = getConsentInputName(
                        consentItem,
                        consentInput
                      );

                      const errorText = errors[consentInputName];

                      return (
                        <ConsentInputWrapper
                          key={consentInputName}
                          size={{ xs: 12, md: 6 }}
                        >
                          <StyleFormHelperText>
                            {consentInput.description}
                          </StyleFormHelperText>

                          <InputField
                            fullWidth
                            id={consentInputName}
                            data-testid={`consent-${consentInputName}`}
                            label={consentInput?.label}
                            error={!!errorText}
                            helperText={t(errorText)}
                            required={consentInput?.isMandatory}
                            value={consentValues?.[consentInputName]}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onChange={(
                              event: ChangeEvent<
                                HTMLInputElement | HTMLTextAreaElement
                              >
                            ) => {
                              handleConsentInputChange(event, consentInput);
                            }}
                            onBlur={() => {
                              validateConsentInputChange();
                            }}
                          />
                        </ConsentInputWrapper>
                      );
                    }
                  )}
                </Grid>
              )}
          </ConsentItemGrid>
        );
      })}
    </>
  );
};

const ConsentItemGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ConsentItemCheckbox = styled(FormControlLabel)({
  width: 'fit-content',
  alignItems: 'flex-start',
});

const ConsentItemLabel = styled(Box)({
  position: 'relative',
});

const ConsentItemDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'block',
  whiteSpace: 'pre-line',
}));

const ConsentInputWrapper = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const InputField = styled(TextField)(({ theme }) => ({
  '.MuiInputLabel-asterisk': {
    color: theme.palette.error.main,
  },
}));

const StyleFormHelperText = styled(FormHelperText)(() => ({
  whiteSpace: 'pre-line',
}));
